import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { DraggableDialogTitle } from "../../positions-and-orders/dialogs/DraggableDialogTitle";
import { DraggableDialogPaper } from "../../positions-and-orders/dialogs/DraggableDialogPaper";
import { useAuthContext } from "../../../features/authentication/hooks/use-auth/AuthContext";
import { httpClient } from "../../shared/services/httpClient";

interface BinanceSecretsDto {
    ApiKey: string;
    SecretKey: string;
}

interface BinanceSecrets {
    apiKey: string;
    secretKey: string;
}

const emptySecrets: BinanceSecrets = {
    apiKey: '',
    secretKey: '',
};

const hasSecrets = (secrets: BinanceSecrets): boolean =>
    secrets.apiKey !== '' || secrets.secretKey !== '';

const areSecretsEqual = (left: BinanceSecrets, right: BinanceSecrets): boolean =>
    left.apiKey === right.apiKey && left.secretKey === right.secretKey;

const mapFromDto = (dto: BinanceSecretsDto): BinanceSecrets => ({
    apiKey: dto.ApiKey ?? '',
    secretKey: dto.SecretKey ?? '',
});

const mapToDto = (secrets: BinanceSecrets): BinanceSecretsDto => ({
    ApiKey: secrets.apiKey,
    SecretKey: secrets.secretKey,
});

export function ApplicationSettingsDialog({
    isOpen,
    setIsOpen,
    icon
}: {
    isOpen: boolean,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    icon: React.ReactNode;
}) {
    const {userAuthData, isUserAuthenticated, binanceAuthData, setBinanceUserAuthData} = useAuthContext();

    const [apiKey, setApiKey] = useState<string>('');
    const [secretKey, setSecretKey] = useState<string>('');
    const [showApiKey, setShowApiKey] = useState<boolean>(false);
    const [showSecretKey, setShowSecretKey] = useState<boolean>(false);
    const [serverSecrets, setServerSecrets] = useState<BinanceSecrets>(emptySecrets);
    const [isServerSecretsLoading, setIsServerSecretsLoading] = useState<boolean>(false);

    const currentSecrets = {
        apiKey,
        secretKey,
    };
    const browserSecrets = {
        apiKey: binanceAuthData.apiKey,
        secretKey: binanceAuthData.secretKey,
    };

    const applyFieldsFromStorage = useCallback((browser: BinanceSecrets, server: BinanceSecrets) => {
        const secrets = hasSecrets(browser) ? browser : server;
        
        setApiKey(secrets.apiKey);
        setSecretKey(secrets.secretKey);
    }, []);
    
    const loadServerSecrets = useCallback(async () => {
        const currentBrowserSecrets = {
            apiKey: binanceAuthData.apiKey,
            secretKey: binanceAuthData.secretKey,
        };
        
        if (!isUserAuthenticated() || userAuthData === null || userAuthData.jwtToken === null) {
            setServerSecrets(emptySecrets);
            applyFieldsFromStorage(currentBrowserSecrets, emptySecrets);
            return;
        }

        setIsServerSecretsLoading(true);
        
        try {
            const response = await httpClient.instance.get<BinanceSecretsDto>(
                'api/users/binance-secrets',
                {
                    headers: { Authorization: `Bearer ${userAuthData.jwtToken}`},
                });
            const loadedServerSecrets = mapFromDto(response.data);
            
            setServerSecrets(loadedServerSecrets);
            applyFieldsFromStorage(currentBrowserSecrets, loadedServerSecrets);
        } catch (error: any) {
            console.error(error);
            setServerSecrets(emptySecrets);
            applyFieldsFromStorage(currentBrowserSecrets, emptySecrets);
        } finally {
            setIsServerSecretsLoading(false);
        }
    }, [
        applyFieldsFromStorage,
        binanceAuthData.apiKey,
        binanceAuthData.secretKey,
        isUserAuthenticated,
        userAuthData
    ]);
    
    useEffect(() => {
        if (isOpen) {
            loadServerSecrets();
        }
    }, [isOpen, loadServerSecrets]);

    const hasBrowserSecrets = hasSecrets(browserSecrets);
    const hasServerSecrets = hasSecrets(serverSecrets);
    const areCurrentSecretsComplete = apiKey !== '' && secretKey !== '';
    const wouldDifferFromServer = hasServerSecrets && !areSecretsEqual(currentSecrets, serverSecrets);
    const wouldDifferFromBrowser = hasBrowserSecrets && !areSecretsEqual(currentSecrets, browserSecrets);
    const isSaveToBrowserDisabled =
        !areCurrentSecretsComplete ||
        areSecretsEqual(currentSecrets, browserSecrets) ||
        wouldDifferFromServer;
    const isSaveToServerDisabled =
        isServerSecretsLoading ||
        !areCurrentSecretsComplete ||
        areSecretsEqual(currentSecrets, serverSecrets) ||
        wouldDifferFromBrowser;
    
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleSaveBrowserSecrets();
    };

    const handleSaveBrowserSecrets = () => {
        setBinanceUserAuthData(currentSecrets);
    };

    const handleSaveServerSecrets = async () => {
        if (!isUserAuthenticated() || userAuthData === null || userAuthData.jwtToken === null) {
            return;
        }

        const response = await httpClient.instance.put<BinanceSecretsDto>(
            'api/users/binance-secrets',
            mapToDto(currentSecrets),
            {
                headers: { Authorization: `Bearer ${userAuthData.jwtToken}`},
            });
        
        setServerSecrets(mapFromDto(response.data));
    };

    const handleClearBrowserSecrets = () => {
        setBinanceUserAuthData(emptySecrets);
        applyFieldsFromStorage(emptySecrets, serverSecrets);
    };

    const handleClearServerSecrets = async () => {
        if (!isUserAuthenticated() || userAuthData === null || userAuthData.jwtToken === null) {
            return;
        }

        const response = await httpClient.instance.put<BinanceSecretsDto>(
            'api/users/binance-secrets',
            {
                ApiKey: '',
                SecretKey: '',
            },
            {
                headers: { Authorization: `Bearer ${userAuthData.jwtToken}`},
            });
        const clearedServerSecrets = mapFromDto(response.data);
        
        setServerSecrets(clearedServerSecrets);
        applyFieldsFromStorage(browserSecrets, clearedServerSecrets);
    };
    
    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSecretVisibilityMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };
    
    return (
        <>
            <Dialog
                open={isOpen}
                PaperComponent={DraggableDialogPaper}
                PaperProps={{
                    component: 'form',
                    onSubmit: handleSubmit,
                    sx: {
                        minWidth: '520px',
                    },
                }}
            >
                <DraggableDialogTitle
                    icon={icon}
                    title="Application settings"
                    onClose={handleClose}
                />
                <DialogContent sx={{ padding: '20px', paddingBottom: '10px' }}>
                    <Stack direction="column" spacing={2} sx={{ padding: '0px', margin: '0px', width: '100%' }}>
                        <TextField
                            margin="normal"
                            id="apiKey"
                            name="apiKey"
                            label="Binance API key"
                            type={showApiKey ? "text" : "password"}
                            size="small"
                            value={apiKey}
                            onChange={(arg: React.ChangeEvent<HTMLInputElement>) => setApiKey(arg.target.value)}
                            variant="outlined"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showApiKey ? "Hide Binance API key" : "Show Binance API key"}
                                            onClick={() => setShowApiKey((show) => !show)}
                                            onMouseDown={handleSecretVisibilityMouseDown}
                                            edge="end"
                                        >
                                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            margin="normal"
                            id="secretKey"
                            name="secretKey"
                            label="Binance secret key"
                            type={showSecretKey ? "text" : "password"}
                            size="small"
                            value={secretKey}
                            onChange={(arg: React.ChangeEvent<HTMLInputElement>) => setSecretKey(arg.target.value)}
                            fullWidth
                            variant="outlined"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showSecretKey ? "Hide Binance secret key" : "Show Binance secret key"}
                                            onClick={() => setShowSecretKey((show) => !show)}
                                            onMouseDown={handleSecretVisibilityMouseDown}
                                            edge="end"
                                        >
                                            {showSecretKey ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Stack
                            direction="row"
                            spacing={2}
                        >
                            <Button
                                sx={{ flex: 1 }}
                                size="small"
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={isSaveToBrowserDisabled}
                            >
                                Save secrets to browser
                            </Button>
                            <Button
                                sx={{ flex: 1 }}
                                size="small"
                                variant="contained"
                                color="error"
                                type="button"
                                onClick={handleClearBrowserSecrets}
                                disabled={!hasBrowserSecrets}
                            >
                                Clear browser secrets
                            </Button>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={2}
                        >
                            <Button
                                sx={{ flex: 1 }}
                                size="small"
                                variant="contained"
                                color="primary"
                                type="button"
                                onClick={handleSaveServerSecrets}
                                disabled={isSaveToServerDisabled}
                            >
                                Save secrets to server
                            </Button>
                            <Button
                                sx={{ flex: 1 }}
                                size="small"
                                variant="contained"
                                color="error"
                                type="button"
                                onClick={handleClearServerSecrets}
                                disabled={isServerSecretsLoading || !hasServerSecrets}
                            >
                                Clear server secrets
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        size="small"
                        variant="contained"
                        color="primary"
                        type="button"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
